// utils
import { QueryClient } from '@tanstack/react-query';
import { getUnixTime } from 'date-fns';
import { Hex } from 'viem';

import { ClientInputError, ClientWalletError } from '@/lib/errors/client';

import type { LimitOrdersQueryArgs } from '@/hooks/useLimitOrders';

import { trackError } from '@/utils/errors';
import { fetchWithJwt } from '@/utils/fetcher';
import { getDefinitiveCancelMessage, getLimitOrderIdentityKey } from '@/utils/limit-order-identity';
import { EthereumHash, isChainAddress, isEthereumAddress } from '@/utils/models';
import { addOpenLimitOrderTombstones } from '@/utils/open-limit-order-tombstones';
import { routes } from '@/utils/routes';

import { limitOrderSchema } from '../../0x.schema';
import {
  CancelDefinitiveLimitOrderParams,
  CancelOneInchLimitOrderParams,
  LimitOrderIdentity,
  LimitOrderResponse,
  OrderConfigInfo,
} from '../../0x.types';
// abi
import {
  EXCHANGE_PROXY_ADDRESS,
  NULL_ADDRESS,
  NULL_STAKING_POOL,
} from '../../../constants/addresses';
import { getLimitOrderProtocolAddress } from '../../../constants/advancedOrder';
import { SUPPORTED_CHAINS_BY_CHAIN_ID } from '../../../constants/chain';
// config
import { getFeeRecipientAddress, getLimitOrderFeesAmountInUnits } from '../../fees';

export const buildLimitOrder = async (orderConfig: OrderConfigInfo, chainId: number) => {
  let limitOrderFeeAmount = 0n;
  try {
    limitOrderFeeAmount = await getLimitOrderFeesAmountInUnits(
      orderConfig.makerToken,
      orderConfig.takerToken,
      orderConfig.takerAmount.toString(),
      chainId,
    );
  } catch (err: unknown) {
    if (err instanceof Error) {
      trackError(new Error(`Couldn't fetch limit order fee amounts: ${err.message}`));
    }
  }

  return limitOrderSchema.parse({
    ...orderConfig,
    chainId: chainId,
    taker: NULL_ADDRESS,
    verifyingContract: EXCHANGE_PROXY_ADDRESS,
    sender: NULL_ADDRESS,
    feeRecipient: getFeeRecipientAddress(chainId),
    pool: NULL_STAKING_POOL,
    takerTokenFeeAmount: limitOrderFeeAmount,
    expiry: getUnixTime(orderConfig.expiry),
    salt: getUnixTime(new Date()),
  });
};

/**
 * True for any limit-orders query containing the given maker. Used to scope
 * both the optimistic `setQueriesData` write and the post-cancel
 * `invalidateQueries` revalidation to that maker's cached pages, regardless
 * of which chain / pagination slice they hold.
 */
const matchesMaker = (maker: string) => (queryKey: readonly unknown[]) => {
  if (queryKey[0] !== 'limit-orders') return false;
  const args = queryKey[1] as LimitOrdersQueryArgs | undefined;
  const queryMakers = args?.makers?.split(',');
  if (!queryMakers) return false;
  return queryMakers.some((queryMaker) =>
    isEthereumAddress(maker)
      ? queryMaker.toLowerCase() === maker.toLowerCase()
      : queryMaker === maker,
  );
};

type RefreshOpenLimitOrdersOptions = {
  intervalMs?: number;
  maxAttempts?: number;
};

export const refreshOpenLimitOrdersUntilVisible = async (
  queryClient: QueryClient,
  maker: string,
  identity: LimitOrderIdentity,
  { intervalMs = 1_000, maxAttempts = 11 }: RefreshOpenLimitOrdersOptions = {},
): Promise<void> => {
  if (!isChainAddress(maker)) {
    throw new ClientInputError('Invalid maker address');
  }

  const makerPredicate = matchesMaker(maker);
  const matchesMakerPageOne = (queryKey: readonly unknown[]) => {
    if (!makerPredicate(queryKey)) return false;
    const args = queryKey[1] as LimitOrdersQueryArgs;
    return args.page === 1;
  };
  const matchingQueries = queryClient.getQueryCache().findAll({
    predicate: (query) => query.isActive() && matchesMakerPageOne(query.queryKey),
  });
  if (matchingQueries.length === 0) return;

  const identityKey = getLimitOrderIdentityKey(identity);
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    await queryClient.refetchQueries({
      type: 'active',
      predicate: (query) => matchesMakerPageOne(query.queryKey),
    });

    const isVisible = queryClient
      .getQueryCache()
      .findAll({
        predicate: (query) => query.isActive() && matchesMakerPageOne(query.queryKey),
      })
      .some((query) => {
        const response = query.state.data as LimitOrderResponse | undefined;
        return response?.records.some((record) => getLimitOrderIdentityKey(record) === identityKey);
      });
    if (isVisible) return;
  }
};

export const invalidateOpenLimitOrders = async (
  queryClient: QueryClient,
  maker: string,
  identities: LimitOrderIdentity[],
): Promise<void> => {
  if (!isChainAddress(maker)) {
    throw new ClientInputError('Invalid maker address');
  }

  addOpenLimitOrderTombstones(identities);

  const cancelledOrderHashes = new Set(identities.map(getLimitOrderIdentityKey));
  const predicate = matchesMaker(maker);

  // Optimistically remove the cancelled orders from each cached page so the
  // UI updates instantly before the server confirms the cancellation.
  queryClient.setQueriesData<LimitOrderResponse | undefined>(
    { predicate: (query) => predicate(query.queryKey) },
    (currentData) => {
      if (!currentData) return currentData;

      const records = currentData.records.filter(
        (record) => !cancelledOrderHashes.has(getLimitOrderIdentityKey(record)),
      );

      if (records.length === currentData.records.length) return currentData;

      return {
        ...currentData,
        records,
        total: Math.max(0, currentData.total - (currentData.records.length - records.length)),
      };
    },
  );

  const response = await fetchWithJwt(routes.api.LIMIT_ORDERS_INVALIDATE, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ maker }),
  });

  if (!response.ok) {
    throw new Error(`Failed to invalidate open limit orders: ${response.status}`);
  }

  // Refetch the maker's cached pages to reconcile with the canonical server state.
  await queryClient.invalidateQueries({
    predicate: (query) => predicate(query.queryKey),
  });
};

/**
 * Cancel a single 1inch open limit order
 */
export const cancelOneInchLimitOrder = async ({
  order,
  chainId,
  walletClient,
}: CancelOneInchLimitOrderParams): Promise<EthereumHash> => {
  if (!walletClient) {
    throw new ClientWalletError('No wallet client detected');
  }

  if (!chainId || chainId !== order.chainId) {
    throw new ClientInputError(
      `Incorrect chainId, detected ${chainId ?? 'none'} but need to cancel order on chain ${
        order.chainId
      }`,
    );
  }

  const cancellationABI = [
    {
      inputs: [
        { internalType: 'MakerTraits', name: 'makerTraits', type: 'uint256' },
        { internalType: 'bytes32', name: 'orderHash', type: 'bytes32' },
      ],
      name: 'cancelOrder',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
  ];

  const [address] = await walletClient.getAddresses();

  // `cancelOrder` is an idempotent storage write on the LOP with no revert
  // conditions for our orders (allowPartialFills + allowMultipleFills => the
  // remaining-invalidator path), so a pre-flight `simulateContract` adds no
  // safety. That extra eth_call was the source of intermittent transient RPC
  // failures (generic "Unexpected error") that aborted the cancel before the
  // tx was ever sent, so we write directly instead. (MAT-6852)
  const txHash = await walletClient.writeContract({
    abi: cancellationABI,
    account: address,
    address: getLimitOrderProtocolAddress(chainId),
    chain: SUPPORTED_CHAINS_BY_CHAIN_ID[chainId],
    functionName: 'cancelOrder',
    args: [BigInt(order.makerTraits), order.orderHash as Hex],
  });

  return txHash;
};

export const cancelDefinitiveLimitOrder = async ({
  order,
  signMessage,
}: CancelDefinitiveLimitOrderParams): Promise<void> => {
  const cancelMessage = getDefinitiveCancelMessage(order.orderId);
  const userSignature = await signMessage(cancelMessage);
  const response = await fetchWithJwt(routes.api.LIMIT_ORDER_CANCEL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      provider: 'definitive',
      orderId: order.orderId,
      cancelMessage,
      userSignature,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to cancel Definitive limit order: ${response.status}`);
  }
};
